<template>
    <div v-if="project" class="space-y-4">
        <FlexibleCard headline-id="appointment" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('appointment')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('appointment')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('appointment')"
                    @enter-edit-mode="onEnterEditMode('appointment', $t('appointment'))"
                    @leave-edit-mode="leaveEditMode('appointment')"
                    @discard-changes="discardChanges('appointment')"
                    @save="saveForm('appointment')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('appointment') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectAppointmentForm
                        v-if="showForm('appointment')"
                        ref="projectAppointmentFormRef"
                        :project-id="project.id"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('project-notes-dayorder')"
                            :text="valueToString(project.notesDayOrder)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('project-notes-appointment')"
                            :text="valueToString(project.notesAppointment)"
                            :has-border-bottom="true"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="location" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('location')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('location')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('location')"
                    @enter-edit-mode="onEnterEditMode('location', $t('location'))"
                    @leave-edit-mode="leaveEditMode('location')"
                    @discard-changes="discardChanges('location')"
                    @save="saveForm('location')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('location') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectLocationForm
                        v-if="showForm('location')"
                        ref="projectLocationFormRef"
                        :project-id="project.id"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('location-requirements')"
                            :text="valueToString(project.locationRequirements)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-setup')"
                            :text="valueToString(project.allocationHints)"
                            :has-border-bottom="true"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>

<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useFormRegistry } from '@/event/forms/useFormRegistry';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { valueToString } from '@/forms/forms';
import { $t } from '@/plugins/fluent';
import { useProjectInjection } from '@/project/composables/useProjectInjection';
import ProjectAppointmentForm from '@/project/forms/project/ProjectAppointmentForm.vue';
import ProjectLocationForm from '@/project/forms/project/ProjectLocationForm.vue';
import { useFluent } from 'fluent-vue';
import { onUnmounted, useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const fluent = useFluent();
const { project } = useProjectInjection();

const {
    activeCard,
    resetActiveCard,
    onEnterEditMode,
    showForm,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
    getCardStatus,
} = useActiveCard();

const projectAppointmentFormRef = useTemplateRef('projectAppointmentFormRef');
const projectLocationFormRef = useTemplateRef('projectLocationFormRef');
const { register, saveForm, discardChanges, leaveEditMode, resetAll } = useFormRegistry();
register('appointment', projectAppointmentFormRef);
register('location', projectLocationFormRef);

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) {
            return false;
        }
        resetAll();
    }
    resetActiveCard();
    return true;
});
onUnmounted(() => resetActiveCard());
</script>
